import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { authService } from '@/services/auth.service'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card } from '@/components/ui/Card'
import { validatePassword } from '@/utils/validation'
import { ERROR_MESSAGES } from '@/config/constants'
import { ApiError } from '@/types'
import { AppLogo } from '@/components/ui/AppLogo'
import { CheckCircle, Lock, WarningCircle } from 'phosphor-react'

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate()
  const { token } = useParams<{ token: string }>()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<{
    password?: string
    confirmPassword?: string
    general?: string
  }>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isTokenInvalid, setIsTokenInvalid] = useState(false)

  useEffect(() => {
    // Redirect to forgot password if no token
    if (!token) {
      navigate('/login/forgot-password', { replace: true })
    }
  }, [token, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    // Validation
    const newErrors: { password?: string; confirmPassword?: string } = {}

    if (!password) {
      newErrors.password = 'Le mot de passe est requis'
    } else if (!validatePassword(password)) {
      newErrors.password =
        'Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial (@$!%*?&)'
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Veuillez confirmer votre mot de passe'
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    if (!token) {
      setErrors({ general: 'Token invalide ou manquant' })
      return
    }

    setIsLoading(true)
    try {
      await authService.setUpPassword({ token, password })
      setIsSuccess(true)
    } catch (error) {
      // Check if it's an UNAUTHORIZED error (invalid or expired token)
      const isUnauthorized =
        (error instanceof AxiosError &&
          (error.response?.status === 401 ||
            (error.response?.data as ApiError)?.message === 'UNAUTHORIZED' ||
            (error.response?.data as ApiError)?.message === 'Unauthorized')) ||
        (error instanceof Error &&
          (error.message === ERROR_MESSAGES.UNAUTHORIZED || error.message.includes('UNAUTHORIZED')))

      if (isUnauthorized) {
        setIsTokenInvalid(true)
        setErrors({
          general: ERROR_MESSAGES.TOKEN_INVALID_EXPIRED,
        })
      } else {
        const errorMessage =
          error instanceof Error ? error.message : "Une erreur s'est produite. Veuillez réessayer."
        setErrors({
          general: errorMessage,
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[var(--color-gray-50)] via-white to-[var(--color-gray-100)] px-4 py-8 sm:py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10 sm:mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-[var(--color-success)] rounded-3xl mb-6 shadow-lg">
              <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-white" weight="regular" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-text-primary)] mb-3">
              Mot de passe réinitialisé
            </h1>
            <p className="text-base sm:text-lg text-[var(--color-text-secondary)]">
              Votre mot de passe a été réinitialisé avec succès.
              <br />
              Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
            </p>
          </div>

          <Card padding="lg">
            <div className="text-center space-y-6">
              <div className="pt-2">
                <Button onClick={() => navigate('/login')} fullWidth size="lg">
                  Se connecter
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  if (!token) {
    return null // Will redirect in useEffect
  }

  // Show error state for invalid/expired token
  if (isTokenInvalid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[var(--color-gray-50)] via-white to-[var(--color-gray-100)] px-4 py-8 sm:py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10 sm:mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-[var(--color-error)] rounded-3xl mb-6 shadow-lg">
              <WarningCircle className="w-10 h-10 sm:w-12 sm:h-12 text-white" weight="regular" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-text-primary)] mb-3">
              Lien invalide ou expiré
            </h1>
            <p className="text-base sm:text-lg text-[var(--color-text-secondary)]">
              Le lien de réinitialisation est invalide ou a expiré.
              <br />
              Veuillez demander un nouveau lien de réinitialisation.
            </p>
          </div>

          <Card padding="lg">
            <div className="text-center space-y-6">
              <div className="pt-2">
                <Button onClick={() => navigate('/login/forgot-password')} fullWidth size="lg">
                  Demander un nouveau lien
                </Button>
              </div>
              <div className="pt-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  fullWidth
                  size="lg"
                >
                  Retour à la connexion
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[var(--color-gray-50)] via-white to-[var(--color-gray-100)] px-4 py-8 sm:py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-10 sm:mb-12">
          <AppLogo size="lg" className="mx-auto mb-6 shadow-lg" />
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-text-primary)] mb-3">
            Réinitialiser le mot de passe
          </h1>
          <p className="text-base sm:text-lg text-[var(--color-text-secondary)]">
            Entrez votre nouveau mot de passe
          </p>
        </div>

        <Card padding="lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            {errors.general && (
              <div
                className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                role="alert"
              >
                {errors.general}
              </div>
            )}

            <Input
              label="Nouveau mot de passe"
              type="password"
              placeholder="Entrez votre nouveau mot de passe"
              value={password}
              onChange={e => setPassword(e.target.value)}
              error={errors.password}
              required
              autoComplete="new-password"
              autoFocus
              leftIcon={<Lock className="w-5 h-5" weight="regular" />}
              helperText="Au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial"
            />

            <Input
              label="Confirmer le mot de passe"
              type="password"
              placeholder="Confirmez votre nouveau mot de passe"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              error={errors.confirmPassword}
              required
              autoComplete="new-password"
              leftIcon={<Lock className="w-5 h-5" weight="regular" />}
            />

            <div className="pt-2">
              <Button type="submit" fullWidth isLoading={isLoading} size="lg">
                Réinitialiser le mot de passe
              </Button>
            </div>
          </form>
        </Card>

        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-sm sm:text-base text-[var(--color-primary)] hover:text-[var(--color-primary-dark)] font-medium transition-colors"
          >
            ← Retour à la connexion
          </button>
        </div>
      </div>
    </div>
  )
}

